
import React from "react"
import { FormattedMessage, defineMessages, injectIntl } from "react-intl"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import IntegrationConfigurationValue from "../../../state/integration/IntegrationConfigurationValue"
import FeatureToggleComponent from "../FeatureToggleComponent"
import IntlProps from "../../../state/helpers/IntlProps"
import Toggle from "react-toggle"
import { Intl } from "../../../state/helpers/IntlProps"
import ConfigurationValueComponent from "../ConfigurationValueComponent"
import {
    FeatureExternalCheckoutPayment,
    FeatureFullPaymentAndCheckoutOnly,
    EnableHiddenFeatures,
    EnableInboxFeature,
    FeatureInventoryLevel,
    FeatureInventorySync,
    FeaturePaymentSync, FeatureProductImportIgnoreSkuMatching,
} from "../../../state/integration/IntegrationFeatureKeys"

const CONFIGURATION_VALUE_PARTIAL_PAYMENT_STRATEGY = "partialPaymentStrategy"

const PARTIAL_PAYMENT_STRATEGY_OPTION_DO_NOTHING = "do-nothing"
const PARTIAL_PAYMENT_STRATEGY_OPTION_CANCEL_EXTRA_ORDER = "cancel-extra-order"

const PARTIAL_PAYMENT_STRATEGY_VALUES_LABEL = defineMessages({
    [PARTIAL_PAYMENT_STRATEGY_OPTION_DO_NOTHING]: {
        id: "integration.form.edit.shopify.partial_payment_strategy.do_nothing.label",
        defaultMessage: "Don't cancel any payment order after a successful checkout",
    },
    [PARTIAL_PAYMENT_STRATEGY_OPTION_CANCEL_EXTRA_ORDER]: {
        id: "integration.form.edit.shopify.partial_payment_strategy.cancel_extra_payment.label",
        defaultMessage: "Cancel payment orders automatically after a successful checkout",
    },
})

const PARTIAL_PAYMENT_STRATEGY_VALUES_DESCRIPTION = {
    [PARTIAL_PAYMENT_STRATEGY_OPTION_DO_NOTHING]: <FormattedMessage
        id='integration.form.edit.shopify.partial_payment_strategy.do_nothing.description'
        defaultMessage="Every successful online checkout attempt will result in a new Shopify order
        that contains a miscellaneous payment/deposit item. {linebreak}This order
        won't be automatically canceled."
        values={{
            linebreak: <br></br>
        }}
    />,
    [PARTIAL_PAYMENT_STRATEGY_OPTION_CANCEL_EXTRA_ORDER]: <FormattedMessage
        id='integration.form.edit.shopify.partial_payment_strategy.cancel_extra_payment.description'
        defaultMessage="Every successful online checkout attempt will result in a new
        Shopify order that contains a miscellaneous payment/deposit item.
        {linebreak}This order will be automatically canceled upon confirmation of a successful
        checkout when paying for an invoice. {linebreak}If the checkout was made for a deposit, the corresponding
        Shopify order will be automatically canceled when the deposit is fully applied in QuoteMachine."
        values={{
            linebreak: <br></br>
        }}
    />,
}

const CONFIGURATION_VALUE_SHORT_DESCRIPTION_METAFIELD = "productShortDescriptionMetafield"

type OwnProps = {
    integrationConfigurationId: string,
    configuration: object,
    configurationValues: IntegrationConfigurationValue[],
    enabledFeatures: object,
    onFeatureToggled: (featureKey: string) => void,
    onConfigurationChange: (configuration: object) => void,
    intl: Intl,
}

const ShopifyFormComponent = (props: OwnProps & IntlProps) => {
    return <div className="col-12 ui-module">
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="realmId">
                <FormattedMessage
                    id="integration.form.edit.account_id"
                    defaultMessage="Account ID"
                />
            </label>
            <div className="col-9">
                <input
                    name="accountId"
                    value={props.configuration["accountId"]}
                    disabled={true}
                    id="accountId"
                    type="text"
                    className="form-control"
                />
            </div>
        </div>
        {renderFeatureToggles(props)}

        {renderConfigurationValues(props)}

        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="configurationExportedInvoicesFulfilled">
                <FormattedMessage
                    id="integration.form.edit.shopify.isInvoiceExportedAsFulfilled"
                    defaultMessage="Export invoices as fulfilled"
                />
            </label>
            <div className="col-9 d-flex align-items-center">
                <Toggle
                    checked={props.configuration["isInvoiceExportedAsFulfilled"]}
                    defaultChecked={props.configuration["isInvoiceExportedAsFulfilled"]}
                    icons={false}
                    onChange={() => {
                        props.onConfigurationChange({
                            ...props.configuration,
                            ...{ isInvoiceExportedAsFulfilled: !props.configuration["isInvoiceExportedAsFulfilled"] },
                        })
                    }}
                />
            </div>
        </div>
    </div>

}

const renderConfigurationValues = (props: OwnProps) => {
    return props.configurationValues.map((c, i) => {
        if (!shouldRenderConfigurationAttribute(c.getKey(), props.enabledFeatures)) {
            return <></>
        }

        if (c.getKey() === CONFIGURATION_VALUE_PARTIAL_PAYMENT_STRATEGY) {
            for (const valueKey of Object.keys(c.getValues())) {
                c.setValueLabel(valueKey, props.intl.formatMessage(PARTIAL_PAYMENT_STRATEGY_VALUES_LABEL[valueKey]))
            }

            return (
                <ConfigurationValueComponent
                    key={i}
                    value={props.configuration[c.getKey()]}
                    configurationValue={c}
                    onChange={(newValue) => {
                        const conf = props.configuration
                        conf[c.getKey()] = newValue
                        props.onConfigurationChange(conf)
                    }}
                    renderConfigurationValueLabel={renderConfigurationValueLabel}
                    valuesDescription={PARTIAL_PAYMENT_STRATEGY_VALUES_DESCRIPTION}
                    hideDefaultOption={true}
                />
            )
        }

        return (
            <ConfigurationValueComponent
                key={i}
                value={props.configuration[c.getKey()]}
                configurationValue={c}
                onChange={(newValue) => {
                    const conf = props.configuration
                    conf[c.getKey()] = newValue
                    props.onConfigurationChange(conf)
                }}
                renderConfigurationValueLabel={renderConfigurationValueLabel}
            />
        )
    })
}

const renderConfigurationValueLabel = (key: string) => {
    switch (key) {
    case CONFIGURATION_VALUE_PARTIAL_PAYMENT_STRATEGY:
        return <FormattedMessage
            id="integration.form.edit.lightspeed.partial_payment_handling"
            defaultMessage="How to handle the partial payment orders in Shopify"
        />
    case CONFIGURATION_VALUE_SHORT_DESCRIPTION_METAFIELD:
        return <FormattedMessage
            id="integration.form.edit.shopify.short_description_metafield"
            defaultMessage="Product metafield to use as short description"
        />
    default:
        return key
    }
}

const shouldRenderConfigurationAttribute = (configurationValueKey: string, enabledFeatures: object) => {
    switch (configurationValueKey) {
    case CONFIGURATION_VALUE_PARTIAL_PAYMENT_STRATEGY:
        return enabledFeatures[FeatureExternalCheckoutPayment] && !enabledFeatures[FeatureFullPaymentAndCheckoutOnly]
    default: return true
    }
}

const renderFeatureToggles = (props: OwnProps) => {
    return Object.keys(props.enabledFeatures).map((featureKey, i) => {

        if (!shouldRenderFeatureToggle(featureKey, props.enabledFeatures)) {
            return <></>
        }

        return <FeatureToggleComponent
            key={i}
            featureKey={featureKey}
            checked={props.enabledFeatures[featureKey]}
            onFeatureToggled={() => { props.onFeatureToggled(featureKey) }}
            renderFeatureLabel={renderFeatureLabel}

        />
    })
}

const shouldRenderFeatureToggle = (featureKey: string, enabledFeatures: object) => {
    switch (featureKey) {
    case FeaturePaymentSync:
    case FeatureInventoryLevel:
    case FeatureInventorySync:
        return false
    case EnableHiddenFeatures:
        return AppContext.getUserLoggedIn().attributes.superUser
    case FeatureFullPaymentAndCheckoutOnly:
        return enabledFeatures[FeatureExternalCheckoutPayment]
    default: return true
    }
}

const renderFeatureLabel = (key: string) => {
    switch (key) {
    case FeatureFullPaymentAndCheckoutOnly:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.full_payment_and_checkout_only"
            defaultMessage="Only accept full payment at checkout"
        />
    case FeatureExternalCheckoutPayment:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.external_checkout_payment"
            defaultMessage="Allow payments through the Shopify Checkout on proposals"
        />
    case EnableHiddenFeatures:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.enable_hidden_features_description"
            defaultMessage="Enable features that are hidden from the UI (STAFF USER ONLY)"
        />
    case EnableInboxFeature:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.enable_inbox_feature"
            defaultMessage="Enable Inbox"
        />
    case FeatureInventoryLevel:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.inventory_level_verification"
            defaultMessage="Verify product is available at specified location when adding products to orders"
        />
    case FeatureProductImportIgnoreSkuMatching:
        return <FormattedMessage
            id="integration.form.edit.feature_toggle.ignore_match_by_sku"
            defaultMessage="SKUs are not unique"
        />
    default:
        return key
    }
}


export default injectIntl(ShopifyFormComponent)



// WEBPACK FOOTER //
// ./src/integration/form/shopify/ShopifyFormComponent.tsx